import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "../../constants/theme";
import { CalcIcon } from "./CalcIcon";
import "./navigation.css";

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function PopupBotao({ icone, titulo, resumo, cor = C.accent2, children }) {
  const [aberto, setAberto] = useState(false);
  const tituloId = useId();
  const dialogoId = useId();
  const dialogoRef = useRef(null);
  const fecharRef = useRef(null);
  const gatilhoRef = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const dialogo = dialogoRef.current;
    const focoAnterior = document.activeElement;
    const gatilho = gatilhoRef.current;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    fecharRef.current?.focus();

    const elementosFocaveis = () => Array.from(dialogo.querySelectorAll(FOCUSABLE)).filter(elemento => {
      const estilo = window.getComputedStyle(elemento);
      return !elemento.closest("[hidden], [inert]") && estilo.display !== "none" && estilo.visibility !== "hidden";
    });

    const aoTeclar = evento => {
      if (evento.key === "Escape") {
        evento.preventDefault();
        evento.stopPropagation();
        setAberto(false);
      }
      if (evento.key !== "Tab") return;
      const elementos = elementosFocaveis();
      const primeiro = elementos[0];
      const ultimo = elementos[elementos.length - 1];
      if (!primeiro) {
        evento.preventDefault();
        dialogo.focus();
      } else if (evento.shiftKey && (document.activeElement === primeiro || document.activeElement === dialogo)) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && (document.activeElement === ultimo || document.activeElement === dialogo)) {
        evento.preventDefault();
        primeiro.focus();
      }
    };
    const conterFoco = evento => {
      if (!dialogo.contains(evento.target)) (fecharRef.current || dialogo).focus();
    };
    document.addEventListener("keydown", aoTeclar);
    document.addEventListener("focusin", conterFoco);
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.removeEventListener("focusin", conterFoco);
      document.body.style.overflow = overflowAnterior;
      if (focoAnterior?.isConnected) focoAnterior.focus();
      else if (gatilho?.isConnected) gatilho.focus();
    };
  }, [aberto]);

  return (
    <>
      <button type="button" ref={gatilhoRef} className="calc-extra-trigger" onClick={() => setAberto(true)}
        aria-haspopup="dialog" aria-expanded={aberto} aria-controls={aberto ? dialogoId : undefined}
        style={{ "--calc-extra-color": cor }}>
        <span className="calc-extra-label">
          <span className="calc-extra-icon" aria-hidden="true">{icone}</span>
          <span>{titulo}</span>
        </span>
        <span className={`calc-extra-summary${resumo ? " has-value" : ""}`}>
          <span>{resumo || "Adicionar"}</span>
          <CalcIcon name="chevron" size={16} />
        </span>
      </button>

      {aberto && createPortal(
        <div className="calc-modal-backdrop" onMouseDown={evento => {
          if (evento.target === evento.currentTarget) setAberto(false);
        }}>
          <section ref={dialogoRef} id={dialogoId} className="calc-extra-dialog" role="dialog"
            aria-modal="true" aria-labelledby={tituloId} tabIndex={-1} style={{ "--calc-extra-color": cor }}>
            <header className="calc-dialog-header">
              <h2 id={tituloId} className="calc-dialog-title">
                <span className="calc-extra-icon" aria-hidden="true">{icone}</span>
                {titulo}
              </h2>
              <button ref={fecharRef} type="button" className="calc-dialog-close" onClick={() => setAberto(false)}
                aria-label={`Fechar ${titulo}`}>
                <CalcIcon name="close" size={20} />
              </button>
            </header>
            <div className="calc-dialog-body">{children}</div>
          </section>
        </div>, document.body
      )}
    </>
  );
}